<template>
  <div class="column bg_page add_llm_page full_page">
    <woot-tabs :index="selectedTabIndex" @change="onClickTabChange">
      <woot-tabs-item
        v-for="tab in tabs"
        :key="tab.key"
        :name="tab.name"
        :show-badge="false"
      />
    </woot-tabs>
    <div class="content-box page_height">
      <section v-if="selectedTabIndex === 0" class="llm_bot_add_section">
        <aside>
          <InputCard
            :label="$t('CAMPAIGN.LLM_CHATBOT.ADD.NAME.LABEL')"
            class="llm_input_field"
          >
            <InputField
              v-model="llm.name"
              type="text"
              :placeholder="$t('CAMPAIGN.LLM_CHATBOT.ADD.NAME.PLACEHOLDER')"
              :error="
                $v.llm.name.$error
                  ? $t('CAMPAIGN.LLM_CHATBOT.ADD.NAME.ERROR')
                  : ''
              "
              @blur="$v.llm.name.$touch"
            />
          </InputCard>
          <InputCard
            :label="$t('CAMPAIGN.LLM_CHATBOT.ADD.LANGUAGE.LABEL')"
            class="llm_input_field mt_20"
          >
            <MultiselectDropdown
              :label="''"
              :info-list="[]"
              class-name-label="llm_dropdown"
              :error="
                $v.llm.languages.$error
                  ? $t('CAMPAIGN.LLM_CHATBOT.ADD.LANGUAGE.ERROR')
                  : ''
              "
            >
              <multiselect
                v-model="llm.languages"
                class="column small-12"
                track-by="id"
                label="name"
                selected-label
                :options="languages_list"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :hide-selected="true"
                :placeholder="
                  $t('CAMPAIGN.LLM_CHATBOT.ADD.LANGUAGE.PLACEHOLDER')
                "
                :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
                :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
              />
            </MultiselectDropdown>
          </InputCard>

          <InputCard
            :label="$t('CAMPAIGN.LLM_CHATBOT.ADD.CALL_INBOX.LABEL')"
            class="llm_input_field mt_20"
          >
            <MultiselectDropdown class-name-label="llm_dropdown">
              <multiselect
                v-model="llm.inbox_id"
                class="column small-12"
                :options="llmCallInboxes"
                track-by="id"
                label="name"
                :multiple="false"
                :close-on-select="true"
                :clear-on-select="false"
                :hide-selected="true"
                :placeholder="
                  $t('CAMPAIGN.LLM_CHATBOT.ADD.CALL_INBOX.PLACEHOLDER')
                "
                selected-label
                :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
                :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
              />
            </MultiselectDropdown>
          </InputCard>

          <InputCard
            :label="$t('CAMPAIGN.LLM_CHATBOT.ADD.FIRST_MSG.LABEL')"
            class="llm_input_field mt_20"
          >
            <!-- show cann response -->
            <canned-response
              v-if="showMentions && hasSlashCommand"
              top="37"
              :search-key="mentionSearchKey"
              @click="replaceText"
            />

            <InputField
              v-model="llm.first_msg"
              type="textarea"
              class="msg_textarea"
              :placeholder="
                $t('CAMPAIGN.LLM_CHATBOT.ADD.FIRST_MSG.PLACEHOLDER')
              "
              :error="
                $v.llm.first_msg.$error
                  ? $t('CAMPAIGN.LLM_CHATBOT.ADD.FIRST_MSG.ERROR')
                  : ''
              "
              @blur="$v.llm.first_msg.$touch"
              @input="onFirstMsgInput"
            />
          </InputCard>

          <InputCard
            :label="$t('CAMPAIGN.LLM_CHATBOT.ADD.PROMPT_MSG.LABEL')"
            class="llm_input_field mt_20"
          >
            <InputField
              v-model="llm.prompt_msg"
              type="textarea"
              class="msg_textarea prompt_msg"
              :placeholder="
                $t('CAMPAIGN.LLM_CHATBOT.ADD.PROMPT_MSG.PLACEHOLDER')
              "
              :error="
                $v.llm.prompt_msg.$error
                  ? $t('CAMPAIGN.LLM_CHATBOT.ADD.PROMPT_MSG.ERROR')
                  : ''
              "
              @blur="$v.llm.prompt_msg.$touch"
            />
          </InputCard>
        </aside>

        <aside>
          <InputCard
            :label="$t('CAMPAIGN.LLM_CHATBOT.ADD.DOCUMENTS.LABEL')"
            class="llm_input_field"
          >
            <woot-button
              variant="smooth"
              color-scheme="primary"
              size="small"
              @click.stop="openDocumentPanel"
            >
              Add document
            </woot-button>
            <div v-if="isDocumentPanelOpen" class="document_panel">
              <div class="document-panel-container">
                <input
                  v-model="documentSearch"
                  type="text"
                  class="document_search_input"
                  placeholder="Search documents..."
                />

                <div class="type_filter_wrapper">
                  <button
                    class="type_filter_button"
                    @click.stop="toggleTypeFilter"
                  >
                    <span>+ Type</span>
                  </button>
                  <div v-if="isTypeFilterOpen" class="type_filter_dropdown">
                    <label class="type_filter_item">
                      <input
                        v-model="selectedDocTypes"
                        type="checkbox"
                        value="document"
                        class="mb_0"
                      />
                      <fluent-icon icon="document" class="type_icon" />
                      <span>File</span>
                    </label>
                    <label class="type_filter_item">
                      <input
                        v-model="selectedDocTypes"
                        type="checkbox"
                        value="url"
                        class="mb_0"
                      />
                      <fluent-icon icon="globe" class="type_icon" />
                      <span>URL</span>
                    </label>
                  </div>
                </div>

                <div v-if="isLoadingDocuments" class="document-loading">
                  <span class="spinner"></span>
                  <p>Loading documents...</p>
                </div>

                <div v-else-if="documentFetchError" class="document-error">
                  <fluent-icon icon="warning" />
                  <p>{{ documentFetchError }}</p>
                  <button
                    class="retry_button"
                    @click.stop="fetchAvailableDocuments"
                  >
                    Retry
                  </button>
                </div>
                <div v-else class="document-list-wrapper">
                  <ul class="document-list">
                    <li
                      v-for="doc in filteredDocuments"
                      :key="doc.id"
                      class="document-list-item"
                      @click="onSelectDocument(doc)"
                    >
                      <fluent-icon
                        :icon="doc.kind === 'url' ? 'globe' : 'document'"
                      />
                      <div class="document-info">
                        <p class="document-name">{{ doc.name }}</p>
                        <p class="document-detail">{{ doc.detail }}</p>
                      </div>
                    </li>
                    <li
                      v-if="filteredDocuments.length === 0"
                      class="no-documents"
                    >
                      No documents found
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <ul class="mt_10 file_preview">
              <li
                v-for="(file, index) in selectedDocumentsList"
                :key="index"
                class="tool_item my_2 text_12 bg_page my_1 px_6 py_2"
              >
                <span style="display:flex;">
                  <fluent-icon
                    :icon="file.kind === 'url' ? 'globe' : 'document'"
                  />
                </span>
                <span class="font_regular">
                  {{ file.name }}
                </span>
                <span @click.stop="onDeleteDocument(file, index)">
                  <fluent-icon icon="delete" />
                </span>
              </li>
            </ul>
            <div>
              <div v-if="llm">
                <ToggleField
                  v-model="llm.rag"
                  :label="$t('CAMPAIGN.LLM_CHATBOT.ADD.DOCUMENTS.RAG_LABEL')"
                  @onToggle="onToggleRAG"
                />
              </div>
            </div>
          </InputCard>
          <InputCard
            :label="$t('CAMPAIGN.LLM_CHATBOT.ADD.TOOLS.LABEL')"
            class="llm_input_field mt_20"
          >
            <woot-button
              variant="smooth"
              color-scheme="primary"
              size="small"
              @click.stop="openToolPanel"
            >
              {{ $t('CAMPAIGN.LLM_CHATBOT.ADD.TOOLS.BTN_LABEL') }}
            </woot-button>
            <div v-if="isToolPanelOpen" class="tool_panel">
              <div class="tool_panel_container">
                <input
                  v-model="toolSearch"
                  type="text"
                  class="tool_search_input"
                  placeholder="Search tools..."
                  @input="onToolSearchInput"
                />

                <div class="tool_items_container">
                  <div v-if="isLoadingTools" class="tool_loading">
                    <span class="spinner"></span>
                    <p>Loading tools...</p>
                  </div>

                  <div v-else-if="toolFetchError" class="tool_error">
                    <fluent-icon icon="warning" />
                    <p>{{ toolFetchError }}</p>
                    <button
                      class="retry_button"
                      @click.stop="fetchAvailableTools"
                    >
                      Retry
                    </button>
                  </div>

                  <div v-else>
                    <div
                      v-if="filteredTools.length > 0"
                      class="tool_actions mt_12"
                    >
                      <p class="tool_section_title">Available Tools</p>
                      <ul class="tool_list">
                        <li
                          v-for="tool in filteredTools"
                          :key="tool.id"
                          class="tool_list_item"
                          @click="onToolSelect(tool)"
                        >
                          <fluent-icon icon="wrench" />
                          <div class="tool_info">
                            <p class="tool_name">{{ tool.name }}</p>
                            <p v-if="tool.description" class="tool_detail">
                              {{ tool.description }}
                            </p>
                          </div>
                        </li>
                      </ul>
                    </div>

                    <div
                      v-if="filteredTools.length === 0 && toolSearch"
                      class="no_tools"
                    >
                      No tools found
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <ul class="mt_10 file_preview">
              <li
                v-for="(tool, index) in llm.tools"
                :key="index"
                class="tool_item  my_2 text_12 bg_page my_1 px_6 py_2"
              >
                <fluent-icon icon="wrench" />
                <div>
                  <span class="font_regular">
                    {{ tool.name }}
                  </span>
                </div>
                <div>
                  <span @click.stop="onDeleteTool(tool, index)">
                    <fluent-icon icon="delete" />
                  </span>
                </div>
              </li>
            </ul>
          </InputCard>
          <InputCard
            :label="$t('CAMPAIGN.LLM_CHATBOT.ADD.LLM_MODEL.LABEL')"
            class="llm_input_field mt_20"
          >
            <MultiselectDropdown
              class-name-label="llm_dropdown"
              :error="
                $v.llm.llm_model.$error
                  ? $t('CAMPAIGN.LLM_CHATBOT.ADD.LLM_MODEL.ERROR')
                  : ''
              "
            >
              <multiselect
                v-model="llm.llm_model"
                class="column small-12"
                :options="llmModels"
                track-by="id"
                label="name"
                :multiple="false"
                :close-on-select="true"
                :clear-on-select="false"
                :hide-selected="true"
                :placeholder="
                  $t('CAMPAIGN.LLM_CHATBOT.ADD.LLM_MODEL.PLACEHOLDER')
                "
                selected-label
                :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
                :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
              />
            </MultiselectDropdown>
          </InputCard>
          <InputCard
            :label="$t('CAMPAIGN.LLM_CHATBOT.ADD.TEMPERATURE.LABEL')"
            class="llm_input_field mt_20"
          >
            <!-- <RangeInput :disabled="false" :min="0" :max="10" :v-model="llm.temperature" :value="llm.temperature" :id="'range_temperature'" @input="onTemperatureChange" /> -->
            <div class="temperature_range_count mt_6">
              <span class="text_12">0</span>
              <span class="text_14 font_600 text_purple_primary">{{
                llm.temperature
              }}</span>
              <span class="text_12">1</span>
            </div>
            <input
              v-model.number="llm.temperature"
              class="temperature_range"
              type="range"
              min="0.0"
              max="1.0"
              step="0.01"
            />
          </InputCard>

          <!-- dynamic variables  -->
          <InputCard
            :label="$t('CAMPAIGN.LLM_CHATBOT.ADD.DYNAMIC_VAR.LABEL')"
            class="llm_input_field mt_20"
          >
            <woot-button
              class="mt_2"
              variant="smooth"
              color-scheme="primary"
              size="small"
              @click="openAddDynamicVarModal"
            >
              {{
                llm.id
                  ? $t('CAMPAIGN.LLM_CHATBOT.ADD.DYNAMIC_VAR.MODAL_BTN_EDIT')
                  : $t('CAMPAIGN.LLM_CHATBOT.ADD.DYNAMIC_VAR.MODAL_BTN')
              }}
            </woot-button>
            <ul class="mt_10 file_preview">
              <li
                v-for="(item, index) in llm.dynamic_variables"
                :key="index"
                class="tool_item  my_2 text_12 bg_page my_1 px_6 py_2"
              >
                <div>
                  <p class="font_regular">{{ index + 1 }})</p>
                </div>
                <div>
                  <span class="font_regular">
                    {{ item.name }}
                  </span>
                </div>
                <div>
                  <span @click.stop="onDeleteVariable(index)">
                    <fluent-icon icon="delete" />
                  </span>
                </div>
              </li>
            </ul>
          </InputCard>

          <InputCard
            :label="$t('CAMPAIGN.LLM_CHATBOT.ADD.RES_FORMAT.LABEL')"
            class="llm_input_field mt_20"
          >
            <!-- :error="$v.llm.res_format.$error ? $t('CAMPAIGN.LLM_CHATBOT.ADD.RES_FORMAT.ERROR') : ''" -->
            <MultiselectDropdown class-name-label="llm_dropdown">
              <multiselect
                v-model="llm.res_format"
                class="column small-12"
                :options="resFormats"
                track-by="id"
                label="name"
                :multiple="false"
                :close-on-select="true"
                :clear-on-select="false"
                :hide-selected="true"
                :placeholder="
                  $t('CAMPAIGN.LLM_CHATBOT.ADD.RES_FORMAT.PLACEHOLDER')
                "
                selected-label
                :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
                :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
              />
            </MultiselectDropdown>
          </InputCard>
        </aside>
      </section>
      <section v-if="selectedTabIndex === 1" class="llm_bot_add_section">
        <aside>
          <InputCard :label="'Boundary'">
            <MultiselectDropdown class-name-label="llm_dropdown">
              <multiselect
                v-model="llm.voice_settings.boundary"
                class="column small-12"
                :options="booleanOptions"
                track-by="id"
                label="name"
                :multiple="false"
                :close-on-select="true"
                :clear-on-select="false"
                :hide-selected="true"
                :placeholder="'Select option'"
                selected-label
                :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
                :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
              />
            </MultiselectDropdown>
          </InputCard>
          <div class="mt_20">
            <InputCard
              :label="$t('CAMPAIGN.LLM_CHATBOT.ADD.GREET_FIRST.LABEL')"
            >
              <p class="input_subtitle">
                {{ $t('CAMPAIGN.LLM_CHATBOT.ADD.GREET_FIRST.SUBTITLE') }}
              </p>
              <MultiselectDropdown class-name-label="llm_dropdown">
                <multiselect
                  v-model="llm.voice_settings.greet_first"
                  class="column small-12"
                  :options="booleanOptions"
                  track-by="id"
                  label="name"
                  :multiple="false"
                  :close-on-select="true"
                  :clear-on-select="false"
                  :hide-selected="true"
                  :placeholder="'Select option'"
                  selected-label
                  :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
                  :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
                />
              </MultiselectDropdown>
            </InputCard>
          </div>
        </aside>
        <aside>
          <InputCard
            :label="$t('CAMPAIGN.LLM_CHATBOT.ADD.EAGERNESS.LABEL') + ' (0 - 5)'"
            class="llm_input_field"
          >
            <p class="input_subtitle">
              {{ $t('CAMPAIGN.LLM_CHATBOT.ADD.EAGERNESS.SUBTITLE') }}
            </p>
            <div class="slider_count mt_6">
              <span class="text_12">0</span>
              <span class="text_14 font_600 text_purple_primary">{{
                llm.voice_settings.eagerness
              }}</span>
              <span class="text_12">5</span>
            </div>
            <input
              v-model.number="llm.voice_settings.eagerness"
              class="slider_input"
              type="range"
              min="0"
              max="5"
              step="1"
              @blur="$v.llm.voice_settings.eagerness.$touch"
            />
          </InputCard>
          <InputCard
            :label="
              $t('CAMPAIGN.LLM_CHATBOT.ADD.SENSITIVITY.LABEL') + ' (0 - 5)'
            "
            class="llm_input_field mt_20"
          >
            <p class="input_subtitle">
              {{ $t('CAMPAIGN.LLM_CHATBOT.ADD.SENSITIVITY.SUBTITLE') }}
            </p>
            <div class="slider_count mt_6">
              <span class="text_12">0</span>
              <span class="text_14 font_600 text_purple_primary">{{
                llm.voice_settings.sensitivity
              }}</span>
              <span class="text_12">5</span>
            </div>
            <input
              v-model.number="llm.voice_settings.sensitivity"
              class="slider_input"
              type="range"
              min="0"
              max="5"
              step="1"
              @blur="$v.llm.voice_settings.sensitivity.$touch"
            />
          </InputCard>
          <InputCard
            :label="
              $t('CAMPAIGN.LLM_CHATBOT.ADD.SMOOTHNESS.LABEL') + ' (0 - 5)'
            "
            class="llm_input_field mt_20"
          >
            <p class="input_subtitle">
              {{ $t('CAMPAIGN.LLM_CHATBOT.ADD.SMOOTHNESS.SUBTITLE') }}
            </p>
            <div class="slider_count mt_6">
              <span class="text_12">0</span>
              <span class="text_14 font_600 text_purple_primary">{{
                llm.voice_settings.smoothness
              }}</span>
              <span class="text_12">5</span>
            </div>
            <input
              v-model.number="llm.voice_settings.smoothness"
              class="slider_input"
              type="range"
              min="0"
              max="5"
              step="1"
              @blur="$v.llm.voice_settings.smoothness.$touch"
            />
          </InputCard>
          <InputCard
            :label="$t('CAMPAIGN.LLM_CHATBOT.ADD.DELAY.LABEL')"
            class="llm_input_field mt_20"
          >
            <p class="input_subtitle">
              {{ $t('CAMPAIGN.LLM_CHATBOT.ADD.DELAY.SUBTITLE') }}
            </p>
            <InputField
              v-model="llm.voice_settings.delay"
              type="number"
              :placeholder="$t('CAMPAIGN.LLM_CHATBOT.ADD.DELAY.PLACEHOLDER')"
              :error="
                $v.llm.voice_settings.delay.$error
                  ? $t('CAMPAIGN.LLM_CHATBOT.ADD.DELAY.ERROR')
                  : ''
              "
              @blur="$v.llm.voice_settings.delay.$touch"
            />
          </InputCard>
        </aside>
      </section>

      <div class="llm_bot_add_section_footer my_20">
        <woot-button
          :is-loading="uiFlags.isCreating || uiFlags.isUpdating"
          @click="onSave"
        >
          {{ $t('CAMPAIGN.LLM_CHATBOT.ADD.SUBMIT_ACTION.BTN_LABEL') }}
        </woot-button>
      </div>
    </div>
    <AddEvalutionModal
      v-if="isAddEvalutionModalOpen"
      :is-open="isAddEvalutionModalOpen"
      :edit-evalution="selectedEditEvalution"
      :on-close="closeEavlutionModal"
      @onAddEvalution="onAddEvalution"
      @onEditEvalution="onEditEvalution"
    />
    <AddDynamicVariableModal
      v-if="isAddDynamicVarModalOpen"
      :is-open="isAddDynamicVarModalOpen"
      :on-close="closeDynamicVarModal"
      :data-list="llm.dynamic_variables"
      @onAddDynamicVariables="onAddDynamicVariables"
    />
  </div>
</template>

<script>
import InputField from 'components/ui/inputs/InputField.vue';
import { mapGetters } from 'vuex';
import {
  required,
  minLength,
  between,
  minValue,
  integer,
  maxValue,
} from 'vuelidate/lib/validators';
import alertMixin from 'shared/mixins/alertMixin';
import MultiselectDropdown from 'components/ui/inputs/MultiselectDropdown.vue';
import InputCard from 'components/ui/inputs/InputCard.vue';
import AddEvalutionModal from './AddEvalutionModal.vue';
import AddDynamicVariableModal from './AddDynamicVariableModal.vue';
import ToggleField from 'components/ui/inputs/ToggleField.vue';
import {
  LLM_MODELS,
  LLM_LANGUAGES,
  LLM_RESPONSE_FORMAT,
  LLM_AUDIO_LANGS,
  LLM_LANGUAGE_TO_AUDIO_LANG_MAPPING,
} from './llm.constant';
import CannedResponse from 'components/widgets/conversation/CannedResponse.vue';
import ToolsAPI from 'dashboard/api/tools.js';
import knowledges from '../../../../../api/knowledges';

export default {
  components: {
    InputField,
    MultiselectDropdown,
    InputCard,
    ToggleField,
    AddEvalutionModal,
    AddDynamicVariableModal,
    CannedResponse,
  },
  mixins: [alertMixin],
  props: {
    prefilledData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedTabIndex: 0,
      search_tool_name: null,
      languages_list: LLM_LANGUAGES,
      isAddEvalutionModalOpen: false,
      isAddDynamicVarModalOpen: false,
      resFormats: LLM_RESPONSE_FORMAT,
      booleanOptions: [
        { id: true, name: 'True' },
        { id: false, name: 'False' },
      ],
      isToolPanelOpen: false,
      createToolType: '',
      isDocumentPanelOpen: false,
      isTypeFilterOpen: false,
      documentSearch: '',
      selectedDocTypes: [],
      availableDocuments: [],
      isLoadingDocuments: false,
      documentFetchError: null,
      toolSearch: '',
      isLoadingTools: false,
      toolFetchError: null,
      toolBtns: [
        {
          id: 'webhook',
          title: 'Add webhook tool',
          icon: 'document-checkmark',
        },
        { id: 'client', title: 'Add client tool', icon: 'wrench' },
      ],
      systemTools: [],
      outsideClickListenerAdded: false,
      llm: {
        name: '',
        languages: [],
        first_msg: '',
        template_name: '',
        prompt_msg: '',
        selectedFiles: [],
        file_to_remove: [], // file id list
        llm_model: null,
        res_format: null,
        temperature: 0.5,
        rag: false,
        tools: [],
        remove_tools: [],
        inbox_id: null,
        attempts: 3,
        interval: 24,
        voice_settings: {
          turnTimeout: 5,
          endCallTimeout: 7,
          maxCallDuration: 3,
          audio_language: [LLM_AUDIO_LANGS[0]],
          eagerness: 1,
          sensitivity: 0,
          smoothness: 0,
          delay: 22000,
          boundary: { id: false, name: 'False' },
          greet_first: { id: false, name: 'False' },
        },
        voice_evaluations: [],
        dynamic_variables: [],
      },
      selectedEditTool: {},
      selectedEditEvalution: {},
      showMentions: false,
      hasSlashCommand: false,
      mentionSearchKey: '',
      selected_system_tool: null,
      globalTools: [],
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      getAccount: 'accounts/getAccount',
      uiFlags: 'llmbots/getUIFlags',
      llm_bots: 'llmbots/getLLMBots',
      llmCallInboxes: 'llmbots/getCallInboxes',
      inboxes: 'inboxes/getInboxes',
    }),
    llmModels() {
      if (!this.currentAccount.llm_hook_configured) {
        return LLM_MODELS.filter(model =>
          ['voice-agent', '8nabler-agent'].includes(model.id)
        );
      }

      return LLM_MODELS;
    },
    isEditMode() {
      return !!this.prefilledData.id;
    },
    currentAccount() {
      return this.getAccount(this.currentUser.account_id);
    },
    tabs() {
      const allTabs = [
        {
          key: 0,
          name: this.$t('CAMPAIGN.LLM_CHATBOT.TABS.AGENT'),
        },
      ];
      if (this.currentAccount?.features?.llm_voice) {
        allTabs.push({
          key: 1,
          name: this.$t('CAMPAIGN.LLM_CHATBOT.TABS.VOICE'),
        });
      }
      return allTabs;
    },
    filteredDocuments() {
      let docs = this.availableDocuments;

      // Filter by type
      if (this.selectedDocTypes.length > 0) {
        docs = docs.filter(doc => this.selectedDocTypes.includes(doc.kind));
      }

      // Filter by search
      if (this.documentSearch) {
        const search = this.documentSearch.toLowerCase();
        docs = docs.filter(
          doc =>
            doc.name.toLowerCase().includes(search) ||
            (doc.detail && doc.detail.toLowerCase().includes(search))
        );
      }

      return docs;
    },
    filteredTools() {
      let tools = this.globalTools;

      // Filter out already selected tools
      const selectedToolNames = this.llm.tools.map(t => t.name.toLowerCase());
      tools = tools.filter(
        tool => !selectedToolNames.includes(tool.name.toLowerCase())
      );

      // Filter by search
      if (this.toolSearch) {
        const search = this.toolSearch.toLowerCase();
        tools = tools.filter(
          tool =>
            tool.name.toLowerCase().includes(search) ||
            (tool.description &&
              tool.description.toLowerCase().includes(search))
        );
      }

      return tools;
    },
    selectedDocumentsList() {
      // Map selected IDs to full document objects from available documents
      return this.llm.selectedFiles
        .map(id => {
          if (typeof id === 'object') return id; // Already an object (existing files)
          return this.availableDocuments.find(doc => doc.id === id);
        })
        .filter(Boolean);
    },
  },
  watch: {
    'llm.first_msg'(updatedMessage) {
      this.hasSlashCommand = updatedMessage && updatedMessage[0] === '/';
      const hasNextWord = updatedMessage && updatedMessage.includes(' ');
      const isShortCodeActive = this.hasSlashCommand && !hasNextWord;
      const selectedInbox = this.inboxes.find(
        el => el.id === this.llm.inbox_id?.id
      );
      const isWhatsAppInbox =
        selectedInbox?.channel_type === 'Channel::Whatsapp';

      if (isShortCodeActive && isWhatsAppInbox) {
        this.mentionSearchKey = updatedMessage.substr(1, updatedMessage.length);
        this.showMentions = true;
      } else if (
        isShortCodeActive &&
        updatedMessage.length < 5 &&
        !isWhatsAppInbox
      ) {
        this.showAlert(
          'Please select only whatsapp inbox to use canned response.'
        );
      } else {
        this.mentionSearchKey = '';
        this.showMentions = false;
        this.llm.first_msg = updatedMessage;
      }
    },
  },
  validations() {
    return {
      llm: {
        name: {
          required,
          minLength: minLength(2),
        },
        languages: {
          required,
          $each: {
            required,
          },
        },
        first_msg: {
          required,
          minLength: minLength(3),
        },
        prompt_msg: {
          required,
          minLength: minLength(5),
        },
        llm_model: {
          required,
        },
        temperature: {
          required,
          between: between(0, 1), // if value must be between 0 and 1
        },
        voice_settings: {
          turnTimeout: {
            validateNumber: value =>
              value === null ||
              value === undefined ||
              value === '' ||
              !Number.isNaN(value),
          },
          endCallTimeout: {
            validateNumber: value =>
              value === null ||
              value === undefined ||
              value === '' ||
              !Number.isNaN(value),
          },
          eagerness: {
            validateNumber: value =>
              value === null || value === '' || !Number.isNaN(value),
            integer,
            minValue: minValue(0),
            maxValue: maxValue(5),
          },

          sensitivity: {
            validateNumber: value =>
              value === null || value === '' || !Number.isNaN(value),
            integer,
            minValue: minValue(0),
            maxValue: maxValue(5),
          },

          smoothness: {
            validateNumber: value =>
              value === null || value === '' || !Number.isNaN(value),
            integer,
            minValue: minValue(0),
            maxValue: maxValue(5),
          },

          delay: {
            validateNumber: value =>
              value === null || value === '' || !Number.isNaN(value),
            integer,
            minValue: minValue(0),
          },
        },
      },
    };
  },
  async mounted() {
    // check if allowd or not to configure from account

    await this.$store.dispatch('llmbots/fetchCallInboxed');
    this.$store.dispatch('attributes/fetchLlmContactAttributes');

    if (this.prefilledData?.id) {
      await this.$nextTick();
      this.llm = {
        ...this.llm,
        id: this.prefilledData.id,
        name: this.prefilledData.name,
        languages: this.prefilledData.languages.map(
          langCode =>
            this.languages_list.find(lang => lang.id === langCode) ||
            this.languages_list.find(
              lang => lang.id === LLM_LANGUAGE_TO_AUDIO_LANG_MAPPING[langCode]
            ) || { id: langCode, name: langCode }
        ),
        first_msg: this.prefilledData.first_msg,
        template_name: this.prefilledData.template_name,
        prompt_msg: this.prefilledData.prompt_msg,
        selectedFiles: this.prefilledData.rag_files,
        file_to_remove: [],
        remove_tools: [],
        llm_model: this.llmModels.find(
          model => model.id === this.prefilledData.llm_model
        ),
        res_format: this.resFormats.find(
          el => el.id === this.prefilledData.res_format
        ),
        rag: this.prefilledData.rag || false,
        temperature: this.prefilledData.temperature,
        tools: this.prefilledData.tools,
        inbox_id: this.llmCallInboxes.find(
          el => el.id === this.prefilledData.inbox_id
        ),
        attempts: this.prefilledData?.attempts || 3,
        interval: this.prefilledData?.interval || 24,
        voice_settings: {
          eagerness:
            this.prefilledData.voice_settings?.eagerness != null
              ? Math.floor(this.prefilledData.voice_settings.eagerness)
              : this.llm.voice_settings.eagerness,
          sensitivity:
            this.prefilledData.voice_settings?.sensitivity != null
              ? Math.floor(this.prefilledData.voice_settings.sensitivity)
              : this.llm.voice_settings.sensitivity,
          smoothness:
            this.prefilledData.voice_settings?.smoothness != null
              ? Math.floor(this.prefilledData.voice_settings.smoothness)
              : this.llm.voice_settings.smoothness,
          delay:
            this.prefilledData.voice_settings?.delay ||
            this.llm.voice_settings.delay,
          boundary: this.prefilledData.voice_settings?.boundary
            ? this.booleanOptions.find(
                o => o.id === this.prefilledData.voice_settings.boundary
              )
            : this.booleanOptions.find(o => o.id === false),
          greet_first: this.prefilledData.voice_settings?.greet_first
            ? this.booleanOptions.find(
                o => o.id === this.prefilledData.voice_settings.greet_first
              )
            : this.booleanOptions.find(o => o.id === false),
        },
        voice_evaluations: this.prefilledData.voice_evaluations || [],
        dynamic_variables: this.prefilledData.dynamic_variables || [],
      };
    }
  },
  beforeDestroy() {
    this.removeOutsideClickListener();
  },
  methods: {
    handleClickOutside(event) {
      // Close tool panel if click is outside
      if (this.isToolPanelOpen) {
        const toolPanel = this.$el.querySelector('.tool_panel');
        if (toolPanel && !toolPanel.contains(event.target)) {
          this.isToolPanelOpen = false;
          this.removeOutsideClickListener();
        }
      }

      // Close document panel if click is outside
      if (this.isDocumentPanelOpen) {
        const documentPanel = this.$el.querySelector('.document_panel');
        if (documentPanel && !documentPanel.contains(event.target)) {
          this.isDocumentPanelOpen = false;
          this.isTypeFilterOpen = false;
          this.removeOutsideClickListener();
        }
      }

      // Close type filter if click is outside
      if (this.isTypeFilterOpen) {
        const typeFilterWrapper = this.$el.querySelector(
          '.type_filter_wrapper'
        );
        if (typeFilterWrapper && !typeFilterWrapper.contains(event.target)) {
          this.isTypeFilterOpen = false;
        }
      }
    },
    removeOutsideClickListener() {
      if (this.outsideClickListenerAdded) {
        document.removeEventListener('click', this.handleClickOutside);
        this.outsideClickListenerAdded = false;
      }
    },
    onFirstMsgInput() {
      this.llm.template_name = '';
    },
    async onSave() {
      this.$v.llm.$touch(); // Touch all fields under 'llm'

      if (this.$v.llm.$invalid) {
        console.debug('Validation failed', this.$v.llm);
        return;
      }
      try {
        const formData = new FormData();

        // Append plain fields
        formData.append('name', this.llm.name);
        formData.append('first_msg', this.llm.first_msg);
        formData.append('template_name', this.llm.template_name);
        formData.append('prompt_msg', this.llm.prompt_msg);
        formData.append('llm_model', this.llm.llm_model.id);
        formData.append('res_format', this.llm.res_format?.id);
        formData.append('temperature', this.llm.temperature);
        formData.append('rag', this.llm.rag);
        formData.append('attempts', this.llm.attempts);
        formData.append('interval', this.llm.interval);
        formData.append('inbox_id', this.llm.inbox_id?.id);

        this.llm.languages.forEach(el => {
          formData.append('languages[]', el.id);
        });

        if (this.llm.tools) {
          this.llm.tools.forEach(el => {
            formData.append('tool_ids[]', el.id.toString());
          });
        }
        // remove tools(if have id are existing tool)
        if (this.llm.remove_tools.length > 0) {
          this.llm.remove_tools.forEach(el => {
            if (el.id) {
              formData.append('remove_tools[]', el.id);
            }
          });
        }

        const selectedFileIds = this.llm.selectedFiles.map(file => {
          if (this.llm.file_to_remove.includes(file.id)) {
            return null;
          }

          if (typeof file === 'object' && file.id) {
            return file.id;
          }

          return file;
        });

        if (selectedFileIds.length === 0) {
          formData.append('vector_files[]', '');
        } else {
          selectedFileIds
            .filter(fileId => fileId !== null)
            .forEach(fileId => {
              formData.append('vector_files[]', fileId);
            });
        }

        // add evaluations
        this.llm.voice_evaluations.forEach(el => {
          formData.append('voice_evaluations[]', JSON.stringify(el));
        });

        this.llm.dynamic_variables.forEach(el => {
          formData.append('dynamic_variables[]', JSON.stringify(el));
        });

        const voiceSettings = {
          ...this.llm.voice_settings,
          boundary: this.llm.voice_settings.boundary.id,
          greet_first: this.llm.voice_settings.greet_first.id,
        };
        formData.append('voice_settings', JSON.stringify(voiceSettings));

        // Submit to store
        if (this.prefilledData?.id) {
          await this.$store.dispatch('llmbots/updateLLMbotById', {
            llm_bot_id: this.prefilledData.id,
            payload: formData,
          });
          this.showAlert(
            this.$t('CAMPAIGN.LLM_CHATBOT.ADD.SUBMIT_ACTION.UPDATE_SUCCESS')
          );
        } else {
          await this.$store.dispatch('llmbots/createLLMbot', formData);
          this.showAlert(
            this.$t('CAMPAIGN.LLM_CHATBOT.ADD.SUBMIT_ACTION.CREATE_SUCCESS')
          );
        }

        this.llm = {
          name: '',
          languages: [],
          first_msg: '',
          template_name: '',
          prompt_msg: '',
          selectedFiles: [],
          file_to_remove: [], // file id list
          llm_model: null,
          res_format: null,
          temperature: 0.5,
          rag: true,
          tools: [],
          attempts: 3,
          interval: 24,
          voice_settings: {
            eagerness: 1,
            sensitivity: 0,
            smoothness: 0,
            delay: 22000,
            boundary: { id: false, name: 'False' },
            greet_first: { id: false, name: 'False' },
          },
          voice_evaluations: [],
          dynamic_variables: [],
        };
        this.$router.push({ name: 'llm_bot_list' });
      } catch (error) {
        console.error('Error saving LLM bot:', error);
        this.showAlert(
          error.response?.data?.message ||
            this.$t('CAMPAIGN.LLM_CHATBOT.ADD.SUBMIT_ACTION.ERROR')
        );
      }
    },
    replaceText(message, template_name) {
      setTimeout(() => {
        this.llm.first_msg = message;
        this.llm.template_name = template_name;
      }, 100);
    },
    onClickTabChange(index) {
      this.selectedTabIndex = index;
    },
    onFileChange(files) {
      this.llm.selectedFiles = files;
    },
    onAddTool(tool) {
      this.llm.tools.push(tool);
    },
    onUpdateTool(tool) {
      const newTool = {
        ...tool,
        isUpdate: true,
      };
      // check if tool.id not present, them if it has new_tool_id then replace that as new tool is edited before DB save
      this.llm.tools = this.llm.tools.map(t => {
        // Match either by real DB id or generated new_tool_id
        if (t.id && tool.id && t.id === tool.id) return newTool;
        if (!tool.id && t.new_tool_id && t.new_tool_id === tool.new_tool_id)
          return newTool;
        return t;
      });
    },
    openAddEvalutionModal() {
      this.isAddEvalutionModalOpen = true;
    },
    onAddEvalution(item) {
      this.llm.voice_evaluations.push(item);
      this.closeEavlutionModal();
    },
    onEditEvalution(item) {
      const index = this.llm.voice_evaluations.findIndex(e => e.id === item.id);
      if (index !== -1) {
        this.llm.voice_evaluations.splice(index, 1, item);
      }
      this.closeEavlutionModal();
    },
    onDeleteEvalution(item) {
      this.llm.voice_evaluations = this.llm.voice_evaluations.filter(
        t => t.id !== item.id
      );
    },
    closeEavlutionModal() {
      this.isAddEvalutionModalOpen = false;
      this.selectedEditEvalution = {};
    },
    onEvalutionClick(item) {
      this.selectedEditEvalution = item;
      this.openAddEvalutionModal();
    },
    openAddDynamicVarModal() {
      this.isAddDynamicVarModalOpen = true;
    },
    closeDynamicVarModal() {
      this.isAddDynamicVarModalOpen = false;
    },
    addNewDynamicVarItem(newItem) {
      this.llm.dynamic_variables.push(newItem);
    },
    onAddDynamicVariables(newList) {
      this.llm.dynamic_variables = newList;
      this.closeDynamicVarModal();
    },
    onDeleteVariable(index) {
      this.llm.dynamic_variables.splice(index, 1);
    },
    onTemperatureChange(value) {
      this.llm.temperature = value;
    },
    onDeleteTool(tool) {
      if (tool.id) {
        this.llm.remove_tools.push(tool);
      }

      this.llm.tools = this.llm.tools.filter(t => t.id !== tool.id);
    },
    onDeleteFile(file, index) {
      // remve file by index
      this.llm.selectedFiles.splice(index, 1);
      // put the id in a list "file_to_remove[]"
      if (file.id) {
        this.llm.file_to_remove.push(file);
      }
    },
    onToggleRAG() {
      this.llm.rag = !this.llm.rag;
    },
    truncateText(text, maxLength = 40) {
      return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
    },
    async onToolEnter(value) {
      console.log(value);
      this.isLoadingTools = true;
      this.toolFetchError = null;
      try {
        const { data } = await ToolsAPI.getToolSearch({ q: value });
        this.globalTools = data;
      } catch (error) {
        this.globalTools = [];
        this.toolFetchError =
          error.response?.data?.message || 'Failed to load tools';
        console.error('Error fetching tools:', error);
      } finally {
        this.isLoadingTools = false;
      }
    },
    async fetchAvailableTools() {
      this.isLoadingTools = true;
      this.toolFetchError = null;
      try {
        const { data } = await ToolsAPI.getToolSearch({ q: '' });
        this.globalTools = data;
      } catch (error) {
        console.error('Error fetching tools:', error);
        this.toolFetchError =
          error.response?.data?.message || 'Failed to load tools';
        this.globalTools = [];
      } finally {
        this.isLoadingTools = false;
      }
    },
    onToolSearchInput() {
      if (this.toolSearch.length >= 2 || this.toolSearch.length === 0) {
        this.onToolEnter(this.toolSearch);
      }
    },
    onToolSelect(value) {
      this.search_tool_name = '';
      this.llm.tools.push(value);
      this.isToolPanelOpen = false;
    },
    onToggleSytemTool(tool) {
      const index = this.systemTools.findIndex(el => el.id === tool.id);
      if (index > -1) {
        this.systemTools[index].active = !this.systemTools[index].active;
      }
    },
    openToolPanel() {
      // Close document panel if open
      if (this.isDocumentPanelOpen) {
        this.isDocumentPanelOpen = false;
        this.isTypeFilterOpen = false;
      }

      this.isToolPanelOpen = !this.isToolPanelOpen;
      if (this.isToolPanelOpen) {
        if (this.globalTools.length === 0) {
          this.fetchAvailableTools();
        }
        this.$nextTick(() => {
          if (!this.outsideClickListenerAdded) {
            document.addEventListener('click', this.handleClickOutside);
            this.outsideClickListenerAdded = true;
          }
        });
      } else {
        this.removeOutsideClickListener();
      }
    },
    openDocumentPanel() {
      // Close tool panel if open
      if (this.isToolPanelOpen) {
        this.isToolPanelOpen = false;
      }

      this.isDocumentPanelOpen = !this.isDocumentPanelOpen;
      if (this.isDocumentPanelOpen) {
        if (this.availableDocuments.length === 0) {
          this.fetchAvailableDocuments();
        }
        this.$nextTick(() => {
          if (!this.outsideClickListenerAdded) {
            document.addEventListener('click', this.handleClickOutside);
            this.outsideClickListenerAdded = true;
          }
        });
      } else {
        this.isTypeFilterOpen = false;
        this.removeOutsideClickListener();
      }
    },
    toggleTypeFilter() {
      this.isTypeFilterOpen = !this.isTypeFilterOpen;
      if (this.isTypeFilterOpen) {
        this.$nextTick(() => {
          if (!this.outsideClickListenerAdded) {
            document.addEventListener('click', this.handleClickOutside);
            this.outsideClickListenerAdded = true;
          }
        });
      }
    },
    async fetchAvailableDocuments() {
      this.isLoadingDocuments = true;
      this.documentFetchError = null;
      try {
        const response = await knowledges.getKnowledgeList();
        const { data = [] } = response.data;
        this.availableDocuments = data;
      } catch (error) {
        console.error('Error fetching documents:', error);
        this.documentFetchError =
          error.response?.data?.message || 'Failed to load documents';
        this.availableDocuments = [];
      } finally {
        this.isLoadingDocuments = false;
      }
    },
    onSelectDocument(doc) {
      // Check if already selected
      const isSelected = this.llm.selectedFiles.some(
        file => (typeof file === 'object' ? file.id : file) === doc.id
      );

      if (!isSelected) {
        this.llm.selectedFiles.push(doc.id);
      }

      // Close panel after selection
      this.isDocumentPanelOpen = false;
    },
    onDeleteDocument(doc, index) {
      // Remove from selected files
      this.llm.selectedFiles.splice(index, 1);

      // Add to remove list if it has an id (existing document)
      if (doc.id) {
        this.llm.file_to_remove.push(doc);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~dashboard/assets/scss/widgets/_fonts.scss';
@import '~dashboard/assets/scss/woot';

.llm_bot_add_section_footer {
  width: 100%;
  display: flex;
  justify-content: end;
}

.bg_page {
  background-color: #fff;
}

.llm_bot_add_section {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  @include breakpoint(medium up) {
    grid-template-columns: 1fr 1fr;
  }
}
.file_preview {
  list-style: none;
}

.tool_item {
  display: grid;
  align-items: center;
  grid-template-columns: 20px 1fr 20px;
  gap: 10px;
  cursor: pointer;

  div {
    display: flex;
    align-items: center;
  }

  p {
    margin: 0;
  }
}

.system_item {
  grid-template-columns: 20px 1fr 20px 20px;
}

.temperature_range {
  width: 100%;
  appearance: none;
  height: 6px;
  background-color: transparent;
  border-radius: 3px;
  margin: 0; /* Important to prevent misalignment */
  padding: 0;
}

/* Chrome, Safari, Edge */
.temperature_range::-webkit-slider-runnable-track {
  height: 6px;
  background-color: var(--w-500);
  border-radius: 3px;
}

.temperature_range::-webkit-slider-thumb {
  appearance: none;
  width: 16px;
  height: 16px;
  background-color: #fff;
  border: 2px solid var(--w-500);
  border-radius: 50%;
  margin-top: -5px; /* Align center of thumb with track */
  cursor: pointer;
  position: relative;
}

/* Firefox */
.temperature_range::-moz-range-track {
  height: 6px;
  background-color: var(--w-500);
  border-radius: 3px;
}

.temperature_range::-moz-range-thumb {
  width: 16px;
  height: 16px;
  background-color: #fff;
  border: 2px solid var(--w-500);
  border-radius: 50%;
  cursor: pointer;
  position: relative;
  top: -5px; /* Align center */
}
.temperature_range_count {
  display: flex;
  justify-content: space-between;
}

.slider_count {
  display: flex;
  justify-content: space-between;
}

.slider_input {
  width: 100%;
  appearance: none;
  height: 6px;
  background-color: transparent;
  border-radius: 3px;
  margin: 0;
  padding: 0;
}

/* Chrome, Safari, Edge */
.slider_input::-webkit-slider-runnable-track {
  height: 6px;
  background-color: var(--w-500);
  border-radius: 3px;
}

.slider_input::-webkit-slider-thumb {
  appearance: none;
  width: 16px;
  height: 16px;
  background-color: #fff;
  border: 2px solid var(--w-500);
  border-radius: 50%;
  margin-top: -5px;
  cursor: pointer;
  position: relative;
}

/* Firefox */
.slider_input::-moz-range-track {
  height: 6px;
  background-color: var(--w-500);
  border-radius: 3px;
}

.slider_input::-moz-range-thumb {
  width: 16px;
  height: 16px;
  background-color: #fff;
  border: 2px solid var(--w-500);
  border-radius: 50%;
  cursor: pointer;
  position: relative;
  top: -5px;
}

.tool_panel {
  position: relative;
  margin-top: 8px;

  .tool_panel_container {
    position: absolute;
    z-index: 2;
    top: 0;
    left: 0;
    width: 100%;
    padding: 10px;
    background: white;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
      0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border-radius: 4px;
    animation: slideDown 0.2s ease-out;
  }

  .tool_items_container {
    max-height: 400px;
    overflow-y: auto;
  }
}

.tool_search_input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--s-100);
  border-radius: 4px;
  font-size: 14px;
  margin-bottom: 8px;

  &:focus {
    outline: none;
    border-color: var(--w-500);
  }
}

.tool_actions {
  margin-bottom: 8px;
}

.tool_section_title {
  font-size: 12px;
  font-weight: 600;
  color: var(--s-700);
  margin: 0 0 8px 0;
  padding: 0 4px;
}

.tool_list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.tool_list_item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px;
  cursor: pointer;
  border-radius: 4px;
  font-size: 12px;

  &:hover {
    background: var(--s-50);
  }

  .tool_info {
    flex: 1;
    min-width: 0;

    .tool_name {
      margin: 0;
      font-weight: 500;
      color: var(--s-900);
      word-break: break-word;
    }

    .tool_detail {
      margin: 2px 0 0 0;
      color: var(--s-600);
      font-size: 11px;
      word-break: break-word;
    }
  }
}

.no_tools {
  padding: 16px;
  text-align: center;
  color: var(--s-600);
  font-size: 12px;
}

.tool_loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  gap: 8px;

  .spinner {
    width: 24px;
    height: 24px;
    border: 3px solid var(--s-100);
    border-top-color: var(--w-500);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  p {
    margin: 0;
    font-size: 12px;
    color: var(--s-600);
  }
}

.tool_error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  gap: 8px;

  p {
    margin: 0;
    font-size: 12px;
    color: var(--s-700);
    text-align: center;
  }

  .retry_button {
    margin-top: 8px;
    padding: 6px 12px;
    background: var(--w-500);
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 12px;
    cursor: pointer;

    &:hover {
      background: var(--w-600);
    }
  }
}

.document_panel {
  position: relative;
  margin-top: 8px;

  .document-panel-container {
    position: absolute;
    z-index: 2;
    top: 0;
    left: 0;
    width: 100%;
    padding: 10px;
    background: white;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
      0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border-radius: 4px;
    animation: slideDown 0.2s ease-out;
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.document_search_input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--s-100);
  border-radius: 4px;
  font-size: 14px;
  margin-bottom: 8px;

  &:focus {
    outline: none;
    border-color: var(--w-500);
  }
}

.type_filter_wrapper {
  position: relative;
  margin-bottom: 8px;
}

.type_filter_button {
  padding: 6px 12px;
  background: transparent;
  border: 1px solid var(--s-100);
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background: var(--s-50);
  }
}

.type_filter_dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid var(--s-100);
  border-radius: 4px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  z-index: 3;
  min-width: 150px;
  animation: fadeIn 0.15s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.type_filter_item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 12px;

  &:hover {
    background: var(--s-50);
  }

  input[type='checkbox'] {
    cursor: pointer;
  }

  .type_icon {
    width: 16px;
    height: 16px;
  }
}

.document-list {
  list-style: none;
  padding: 0;
  margin: 0;

  &-wrapper {
    max-height: 400px;
    overflow-y: auto;
  }
}

.document-list-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px;
  cursor: pointer;
  border-radius: 4px;
  font-size: 12px;

  &:hover {
    background: var(--s-50);
  }

  .document-info {
    flex: 1;
    min-width: 0;

    .document-name {
      margin: 0;
      font-weight: 500;
      color: var(--s-900);
      word-break: break-word;
    }

    .document-detail {
      margin: 2px 0 0 0;
      color: var(--s-600);
      font-size: 11px;
      word-break: break-all;
    }
  }
}

.no-documents {
  padding: 16px;
  text-align: center;
  color: var(--s-600);
  font-size: 12px;
}

.document-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  gap: 8px;

  .spinner {
    width: 24px;
    height: 24px;
    border: 3px solid var(--s-100);
    border-top-color: var(--w-500);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  p {
    margin: 0;
    font-size: 12px;
    color: var(--s-600);
  }
}

.document-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  gap: 8px;

  p {
    margin: 0;
    font-size: 12px;
    color: var(--s-700);
    text-align: center;
  }

  .retry_button {
    margin-top: 8px;
    padding: 6px 12px;
    background: var(--w-500);
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 12px;
    cursor: pointer;

    &:hover {
      background: var(--w-600);
    }
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.\!mb_0 {
  margin-bottom: 0 !important;
}
.input_subtitle {
  font-size: 12px;
  color: var(--s-600);
  margin-top: 4px;
  margin-bottom: 4px;
}
</style>

<style lang="scss">
.add_llm_page .llm_input_field .multiselect {
  margin: 2px;
}
.add_llm_page .msg_textarea textarea {
  min-height: 10rem;
}
.add_llm_page .llm_dropdown .message {
  margin-top: 0;
}
.add_llm_page .prompt_msg textarea {
  min-height: 32rem;
}

.add_llm_page {
  .dropdown_slot {
    padding: 0 !important;
  }

  .multiselect__input,
  .multiselect__tags .multiselect__input {
    height: 0 !important;
    visibility: hidden !important;
  }

  .multiselect--active .multiselect__input,
  .multiselect--active .multiselect__tags .multiselect__input {
    padding: 10px 3px !important;
    height: 100% !important;
    visibility: visible !important;
  }
}
</style>
